import { IconMoon, IconSunHigh } from '@tabler/icons-react'

import { Location, Surgery } from '~/storm'

import { getLevelOfCareNprName } from './dips/surgery'
import { isNullish } from './guards'

/**
 * Maps the DIPS level of care npr names to a label and icon.
 */
export const dayOvernightMap = {
    'Poliklinisk dagbehandling': { label: 'Dag', icon: <IconSunHigh className="size-4" stroke={1.5} />, value: 'outpatient' },
    Heldøgn: { label: 'Heldøgn', icon: <IconMoon className="size-4" stroke={1.5} />, value: 'inpatient' },
} as const

type DayOvernight = typeof dayOvernightMap
type DayOvernightKey = keyof DayOvernight
type DayOvernightLabel = DayOvernight[keyof DayOvernight]['label']

export function isDayOvernightKey(key: string | null | undefined): key is DayOvernightKey {
    if (isNullish(key)) return false
    return key in dayOvernightMap
}

export function isDayOvernightLabel(label: string): label is DayOvernightLabel {
    return Object.values(dayOvernightMap).some(dayOvernight => dayOvernight.label === label)
}

/**
 * Maps the DI locations care level names to DIPS level of care npr names.
 */
const locationCareLevelMap: Record<Location['default_care_level'], DayOvernightKey> = {
    outpatient: 'Poliklinisk dagbehandling',
    inpatient: 'Heldøgn',
}

/**
 * Gets the label and icon based on the DI location care level
 */
export function getDayOvernightByLocationCareLevel(careLevel: Location['default_care_level']) {
    return dayOvernightMap[locationCareLevelMap[careLevel]]
}

/**
 * Gets the DI location care level based on the DIPS level of care npr name
 */
export function getLocationCareLevel(surgery: Surgery): Location['default_care_level'] {
    const levelOfCareNprName = getLevelOfCareNprName(surgery)

    if (isDayOvernightKey(levelOfCareNprName)) {
        return dayOvernightMap[levelOfCareNprName].value
    }

    // Based on LDS prod data, there are 4 possible values for levelOfCareName in DIPS Arena:
    // - 'Heldøgn' -> 'Heldøgn' or 'inpatient' in Hero
    // - 'Poliklinisk dagbehandling' -> 'Dag' or 'outpatient' in Hero
    // - 'Poliklinisk omsorg' (not used by LDS)
    // - 'Dagbehandling' (not used by LDS)

    // Type-wise, it can also be nullish (although prod data suggests this is not the case)
    // Hence, we default to 'outpatient' as the unused values are sometimes mistakenly used instead of 'Poliklinisk dagbehandling'

    return 'outpatient'
}

export function getDayOvernightLabel(name: string | null | undefined) {
    if (isDayOvernightKey(name)) {
        return dayOvernightMap[name].label
    }

    return name ? name : null
}

export function surgeryCareIsDay(name: string | null | undefined) {
    return getDayOvernightLabel(name) === 'Dag'
}

export function surgeryCareIsOvernight(name: string | null | undefined) {
    return getDayOvernightLabel(name) === 'Heldøgn'
}

export const dayOvernightChipOptions: Array<{ value: string; label: string }> = Object.entries(dayOvernightMap).map(([value, { label }]) => ({
    value,
    label,
}))
